import { supabase } from '@/integrations/supabase/client';
import { normalizeGhanaPhone, generatePhoneVariations, validateGhanaPhone } from '@/utils/phoneUtils';
import { toast } from 'sonner';

export interface CustomerData {
  firstName: string;
  lastName?: string;
  email?: string;
  phone: string;
  customerType?: 'individual' | 'business';
  address?: string;
  city?: string;
  region?: string;
  companyName?: string;
  notes?: string;
  userId?: string;
}

export interface Customer {
  id: string;
  user_id?: string;
  customer_number: string;
  first_name: string;
  last_name?: string;
  email?: string;
  phone: string;
  customer_type: 'individual' | 'business';
  address?: string;
  city?: string;
  region?: string;
  company_name?: string;
  notes?: string;
  profile_completion_percentage: number;
  total_orders: number;
  total_spent: number;
  status: 'active' | 'inactive' | 'blocked';
  created_at: string;
  updated_at: string;
}

export class CustomerService {
  /**
   * Find existing customer by phone number (handles all formats)
   */
  static async findCustomerByPhone(phone: string): Promise<Customer | null> {
    try {
      const phoneValidation = validateGhanaPhone(phone);
      if (!phoneValidation.isValid) {
        console.warn('Invalid phone number format:', phone);
        return null;
      }

      const normalizedPhone = phoneValidation.normalizedPhone;
      const phoneVariations = generatePhoneVariations(phone);

      console.log('Searching for customer with phone variations:', phoneVariations);

      // Search for customer using any phone variation
      const { data: customers, error } = await supabase
        .from('customers')
        .select('*')
        .in('phone', phoneVariations)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error searching for customer:', error);
        return null;
      }

      if (customers && customers.length > 0) {
        const customer = customers[0];

        // Update the phone number to normalized format if it's different
        if (customer.phone !== normalizedPhone) {
          await this.updateCustomerPhone(customer.id, normalizedPhone);
          customer.phone = normalizedPhone;
        }

        return customer;
      }

      return null;
    } catch (error) {
      console.error('Error in findCustomerByPhone:', error);
      return null;
    }
  }

  /**
   * Create new customer or return existing one by phone
   */
  static async findOrCreateCustomer(customerData: CustomerData): Promise<Customer> {
    try {
      // Validate and normalize phone number
      const phoneValidation = validateGhanaPhone(customerData.phone);
      if (!phoneValidation.isValid) {
        throw new Error(phoneValidation.errorMessage || 'Invalid phone number');
      }

      const normalizedPhone = phoneValidation.normalizedPhone;

      // First, try to find existing customer
      const existingCustomer = await this.findCustomerByPhone(normalizedPhone);

      if (existingCustomer) {
        // Update customer info if provided data has more details
        const updatedCustomer = await this.updateCustomerIfNecessary(existingCustomer, customerData);
        return updatedCustomer;
      }

      // Create new customer
      return await this.createNewCustomer({ ...customerData, phone: normalizedPhone });

    } catch (error: unknown) {
      console.error('Error in findOrCreateCustomer:', error);
      throw error;
    }
  }

  /**
   * Create a new customer record
   */
  static async createNewCustomer(customerData: CustomerData): Promise<Customer> {
    try {
      const normalizedPhone = normalizeGhanaPhone(customerData.phone);
      if (!normalizedPhone) {
        throw new Error('Invalid phone number format');
      }

      // Calculate profile completion
      let profileCompletion = 30; // Base for name and phone
      if (customerData.email?.trim()) profileCompletion += 20;
      if (customerData.lastName?.trim()) profileCompletion += 10;
      if (customerData.address?.trim()) profileCompletion += 15;
      if (customerData.city?.trim()) profileCompletion += 10;
      if (customerData.region?.trim()) profileCompletion += 10;
      if (customerData.customerType === 'business' && customerData.companyName?.trim()) profileCompletion += 5;

      // Generate unique customer number
      const customerNumber = `CUST-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;

      const dbCustomerData = {
        customer_number: customerNumber,
        user_id: customerData.userId || null,
        first_name: customerData.firstName.trim(),
        last_name: customerData.lastName?.trim() || null,
        email: customerData.email?.trim() || null,
        phone: normalizedPhone,
        customer_type: customerData.customerType || 'individual',
        address: customerData.address?.trim() || null,
        city: customerData.city?.trim() || null,
        region: customerData.region?.trim() || null,
        company_name: customerData.companyName?.trim() || null,
        notes: customerData.notes?.trim() || null,
        profile_completion_percentage: profileCompletion,
        profile_completion_discount_applied: false,
        birthday_greeting_sent_this_year: false,
        preferred_communication: customerData.email?.trim() ? 'email' : 'sms',
        marketing_consent: false,
        loyalty_points: 0,
        total_orders: 0,
        total_spent: 0,
        average_order_value: 0,
        status: 'active',
        customer_since: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Creating new customer:', dbCustomerData);

      const { data: customer, error } = await supabase
        .from('customers')
        .insert([dbCustomerData])
        .select()
        .maybeSingle();

      if (error) {
        console.error('Database error creating customer:', error);

        if (error.code === '23505') {
          if (error.message?.includes('customers_phone_key')) {
            // Phone number already exists, try to fetch that customer
            const existingCustomer = await this.findCustomerByPhone(normalizedPhone);
            if (existingCustomer) {
              return await this.updateCustomerIfNecessary(existingCustomer, customerData);
            }
          }
          throw new Error('A customer with these details already exists.');
        }

        throw new Error(`Failed to create customer: ${error.message}`);
      }

      console.log('Customer created successfully:', customer);
      return customer;

    } catch (error: unknown) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  /**
   * Update existing customer if new data provides more information
   */
  static async updateCustomerIfNecessary(existingCustomer: Customer, newData: CustomerData): Promise<Customer> {
    try {
      const updates: Record<string, unknown> = {};
      let hasUpdates = false;

      // Update name if not present or if new data has both first and last name
      if (!existingCustomer.last_name && newData.lastName?.trim()) {
        updates.last_name = newData.lastName.trim();
        hasUpdates = true;
      }

      // Update email if not present
      if (!existingCustomer.email && newData.email?.trim()) {
        updates.email = newData.email.trim();
        hasUpdates = true;
      }

      // Update address if not present
      if (!existingCustomer.address && newData.address?.trim()) {
        updates.address = newData.address.trim();
        hasUpdates = true;
      }

      // Update city if not present
      if (!existingCustomer.city && newData.city?.trim()) {
        updates.city = newData.city.trim();
        hasUpdates = true;
      }

      // Update region if not present
      if (!existingCustomer.region && newData.region?.trim()) {
        updates.region = newData.region.trim();
        hasUpdates = true;
      }

      // Update company name if business type and not present
      if (newData.customerType === 'business' && !existingCustomer.company_name && newData.companyName?.trim()) {
        updates.company_name = newData.companyName.trim();
        updates.customer_type = 'business';
        hasUpdates = true;
      }

      // Append notes if provided
      if (newData.notes?.trim()) {
        const existingNotes = existingCustomer.notes || '';
        const newNote = `[${new Date().toISOString().split('T')[0]}] ${newData.notes.trim()}`;
        updates.notes = existingNotes ? `${existingNotes}\n${newNote}` : newNote;
        hasUpdates = true;
      }

      if (hasUpdates) {
        updates.updated_at = new Date().toISOString();

        const { data: updatedCustomer, error } = await supabase
          .from('customers')
          .update(updates)
          .eq('id', existingCustomer.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating customer:', error);
          return existingCustomer; // Return original if update fails
        }

        console.log('Customer updated with new information:', updatedCustomer);
        return updatedCustomer;
      }

      return existingCustomer;

    } catch (error: unknown) {
      console.error('Error updating customer:', error);
      return existingCustomer; // Return original if update fails
    }
  }

  /**
   * Update customer phone number to normalized format
   */
  private static async updateCustomerPhone(customerId: string, normalizedPhone: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('customers')
        .update({
          phone: normalizedPhone,
          updated_at: new Date().toISOString()
        })
        .eq('id', customerId);

      if (error) {
        console.error('Error updating customer phone:', error);
      }
    } catch (error) {
      console.error('Error in updateCustomerPhone:', error);
    }
  }

  /**
   * Link existing user account to customer record by phone
   */
  static async linkUserToCustomer(userId: string, phone: string): Promise<Customer | null> {
    try {
      const customer = await this.findCustomerByPhone(phone);

      if (customer) {
        // Update customer with user_id if not already linked
        if (!customer.user_id) {
          const { data: updatedCustomer, error } = await supabase
            .from('customers')
            .update({
              user_id: userId,
              updated_at: new Date().toISOString(),
              notes: customer.notes ?
                `${customer.notes}\n[${new Date().toISOString().split('T')[0]}] Linked to user account` :
                `[${new Date().toISOString().split('T')[0]}] Linked to user account`
            })
            .eq('id', customer.id)
            .select()
            .single();

          if (error) {
            console.error('Error linking user to customer:', error);
            throw new Error(`Failed to link user to customer: ${error.message}`);
          }

          console.log('User linked to existing customer:', updatedCustomer);
          return updatedCustomer;
        } else {
          // Customer already has a user_id, check if it's the same
          if (customer.user_id === userId) {
            console.log('Customer already linked to this user');
            return customer;
          } else {
            console.log('Customer already linked to different user');
            throw new Error('This phone number is already linked to another account. Please contact support if this is your phone number.');
          }
        }
      }

      return customer;
    } catch (error: unknown) {
      console.error('Error linking user to customer:', error);
      throw error;
    }
  }

  /**
   * Find customer by user ID
   */
  static async findCustomerByUserId(userId: string): Promise<Customer | null> {
    try {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // No customer found for this user
          return null;
        }
        console.error('Error finding customer by user ID:', error);
        return null;
      }

      return customer;
    } catch (error) {
      console.error('Error in findCustomerByUserId:', error);
      return null;
    }
  }

  /**
   * Automatically link user to customer on login if phone number is available
   */
  static async autoLinkUserOnLogin(userId: string, userMetadata: any): Promise<Customer | null> {
    try {
      // First check if user already has a linked customer
      const existingCustomer = await this.findCustomerByUserId(userId);
      if (existingCustomer) {
        return existingCustomer;
      }

      // Check if user has phone number in metadata
      const userPhone = userMetadata?.phone || userMetadata?.user_metadata?.phone;
      if (!userPhone) {
        console.log('No phone number in user metadata for auto-linking');
        return null;
      }

      // Try to link by phone number
      const linkedCustomer = await this.linkUserToCustomer(userId, userPhone);
      if (linkedCustomer) {
        console.log('Customer auto-linked on login:', linkedCustomer);
        return linkedCustomer;
      }

      return null;
    } catch (error: unknown) {
      console.error('Error in autoLinkUserOnLogin:', error);
      return null;
    }
  }

  /**
   * Get customer order count for phone number
   */
  static async getCustomerOrderCount(phone: string): Promise<number> {
    try {
      const customer = await this.findCustomerByPhone(phone);
      if (!customer) return 0;

      const { count, error } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customer.id);

      if (error) {
        console.error('Error getting order count:', error);
        return customer.total_orders || 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getCustomerOrderCount:', error);
      return 0;
    }
  }

  /**
   * Update customer order statistics
   */
  static async updateCustomerStats(customerId: string, orderAmount: number): Promise<void> {
    try {
      // Get current customer data
      const { data: customer, error: fetchError } = await supabase
        .from('customers')
        .select('total_orders, total_spent')
        .eq('id', customerId)
        .single();

      if (fetchError || !customer) {
        console.error('Error fetching customer for stats update:', fetchError);
        return;
      }

      const newTotalOrders = (customer.total_orders || 0) + 1;
      const newTotalSpent = (customer.total_spent || 0) + orderAmount;
      const newAverageOrderValue = newTotalSpent / newTotalOrders;

      const { error: updateError } = await supabase
        .from('customers')
        .update({
          total_orders: newTotalOrders,
          total_spent: newTotalSpent,
          average_order_value: newAverageOrderValue,
          last_order_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', customerId);

      if (updateError) {
        console.error('Error updating customer stats:', updateError);
      }
    } catch (error) {
      console.error('Error in updateCustomerStats:', error);
    }
  }
}

export default CustomerService;